import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button, Input } from '../../genericComponents'
import { toast } from 'react-toastify'
import { paymentApiURL } from '../../../helpers/initializeServices';
import { useHistory } from "react-router-dom";
import { Context } from '../../../context/globalContext';
import { useMutation } from '@apollo/client';
import { CANCEL_STRIPE_SUBSCRIPTION, CREATE_STRIPE_SUBSCRIPTION, RETRY_STRIPE_SUBSCRIPTION } from '../../../helpers/graphqlMutations';


export default function CreditCardCheckoutForm({ membership, showCheckout, cancelClick, stripeSubscription, retryPayment }) {
  const {
    state: { user },
    dispatch: { setUser }
  } = useContext(Context);
  let history = useHistory()
  const stripe = useStripe();
  const elements = useElements();

  const billingInitialState = {
    email: user.email,
    address: {}
  }

  const [retrySubscriptionMutation] = useMutation(RETRY_STRIPE_SUBSCRIPTION, { onCompleted: createStripeSubscriptionCompleted, onError: createStripeSubscriptionError })
  const [createSubscriptionMutation] = useMutation(CREATE_STRIPE_SUBSCRIPTION, { onCompleted: createStripeSubscriptionCompleted, onError: createStripeSubscriptionError })
  const [cancelSubscriptionMutation] = useMutation(CANCEL_STRIPE_SUBSCRIPTION);

  const [billingDetails, setBillingDetails] = useState(billingInitialState);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState({});
  const [cardComplete, setCardComplete] = useState(false);
  const [cardExpComplete, setCardExpComplete] = useState(false);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardCVCComplete, setCardCVCComplete] = useState(false);

  const stripeOnChange = (e) => {
    setError(e.error);
    let isComplete = false;
    switch (e.elementType) {
      case "cardNumber":
        isComplete = cardExpComplete && cardCVCComplete && e.complete
        setCardComplete(isComplete)
        setCardNumberComplete(e.complete);
        break;
      case "cardExpiry":
        isComplete = cardNumberComplete && cardCVCComplete && e.complete
        setCardComplete(isComplete)
        setCardExpComplete(e.complete);
        break;
      case "cardCvc":
        isComplete = cardNumberComplete && cardExpComplete && e.complete
        setCardComplete(isComplete)
        setCardCVCComplete(e.complete);
        break;
      default:
        break;
    }
  };

  const handleOnChange = ({ target }) => {
    const { id, value } = target
    let arrayParam = id.split('_')
    if (arrayParam.length > 1) {
      let param = arrayParam.slice(1).join('_')
      setBillingDetails({ ...billingDetails, address: { ...billingDetails.address, [param]: value } })
    }
    else {
      setBillingDetails({ ...billingDetails, [arrayParam[0]]: value })
    }

  };

  async function handleSubmit(event) {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    if (error || !cardComplete) {
      if (!cardNumberComplete) {
        elements.getElement(CardNumberElement).focus();
      } else if (!cardExpComplete) {
        elements.getElement(CardExpiryElement).focus();
      } else if (!cardCVCComplete) {
        elements.getElement(CardCvcElement).focus();
      }
      return;
    }
    setIsProcessing(true);
    if(retryPayment&&stripeSubscription) return retryPaymentInterval();
    if(stripeSubscription?.id) await cancelSubscriptionMutation({variables:{subscriptionId:stripeSubscription.id,email:user.email}});
    if (membership.payment_interval !== 0) return paymentInterval();
    unicPayment();
  };

  // console.log('stripeSubscription',stripeSubscription)

  async function unicPayment() {
    try {
      const { data: clientSecret } = await axios.post(
        `${process.env.REACT_APP_PAYMENT_API_URL}/paymentprocesing`,
        {
          code: membership.type,
          email: user.email
        },
      );

      const confirmedCardPayment = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          type: 'card',
          card: elements.getElement(CardNumberElement),
          billing_details: billingDetails
        },
      });

      toast.info('Your payment is being processed')
      history.push(`/membership`)
      setIsProcessing(false);
    } catch (error) {
      console.log(error)
    }
  }

  async function retryPaymentInterval() {
    const newPaymentMethod = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement),
      billing_details: billingDetails
    })
    if (newPaymentMethod.error) {
      return toast.error('An error ocurred with your payment method')
    }
    retrySubscriptionMutation({variables:{
      customerId: user.stripe_customer_id,
      paymentMethodId: newPaymentMethod.paymentMethod.id,
      invoiceId: stripeSubscription?.latest_invoice,
    }})
  }

  async function paymentInterval() {
    let customerId;
    if (!user.stripe_customer_id) {
      const { data: { customerId: newCustomerId } } = await axios.post(`${process.env.REACT_APP_PAYMENT_API_URL}/create-stripe-customer`, { email: user.email });
      customerId = newCustomerId;
      setUser({ ...user, stripe_customer_id: newCustomerId });
    } else {
      customerId = user.stripe_customer_id;
    }
    const newPaymentMethod = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement),
      billing_details: billingDetails
    })
    if (newPaymentMethod.error) {
      return toast.error('An error ocurred with your payment method')
    }
    createSubscriptionMutation({variables:{
      customerId: customerId,
      paymentMethodId: newPaymentMethod.paymentMethod.id,
      priceId: membership.stripe_price_id,
    }})
  }

  function createStripeSubscriptionCompleted(data) {
    toast.info('Your payment is being processed')
    history.push(`/membership`)
    setIsProcessing(false);
    if(retryPayment)cancelClick()
  }
  function createStripeSubscriptionError(error) {
    console.log(error.message)
    if(error.message==="card_declined"){
      toast.info('Your card was declined')
    }else{
      toast.info('An error ocurred');
    }
    setIsProcessing(false);
  }

  const resetForm = () => {
    setBillingDetails(billingInitialState)
    setCardComplete(false)
    setCardNumberComplete(false)
    setCardCVCComplete(false)
    setCardExpComplete(false)
    elements.getElement(CardNumberElement).clear()
    elements.getElement(CardExpiryElement).clear()
    elements.getElement(CardCvcElement).clear()
  }


  return (
    <ContentContainer>
      <form onSubmit={handleSubmit}>
        {showCheckout ? (<div>
          <FormGroup style={{ padding: '10px 15px' }}>
            <LabelForInput htmlFor="name">Name on Card</LabelForInput>
            <InputComponent
              type="text"
              id="name"
              value={billingDetails.name || ''}
              onChange={handleOnChange}
              placeholder="Harley Quinn"
            ></InputComponent>
          </FormGroup>

          <FormGroup style={{ padding: '10px 15px' }}>
            <LabelForInput htmlFor="customerName">Card Number</LabelForInput>
            <StripeInputContainer>
              <CardNumberElement options={cardNumberElementOptions}
                onChange={stripeOnChange} />
            </StripeInputContainer>
          </FormGroup>
          <FormGroup>
            <HalfSizeContainer>
              <LabelForInput htmlFor="expiry">Expiry</LabelForInput>
              <StripeInputContainer>
                <CardExpiryElement options={expiryElementOptions}
                  onChange={stripeOnChange} />
              </StripeInputContainer>
            </HalfSizeContainer>
            <HalfSizeContainer>
              <LabelForInput htmlFor="expiry">CVC</LabelForInput>
              <StripeInputContainer>
                <CardCvcElement options={expiryElementOptions}
                  onChange={stripeOnChange} />
              </StripeInputContainer>
            </HalfSizeContainer>
          </FormGroup>
          <Title>
            Billing Information
        </Title>
          <FormGroup style={{ padding: '10px 15px' }}>
            <LabelForInput htmlFor="customerAddress">Street</LabelForInput>
            <InputComponent
              type="text"
              id="address_line1"
              value={billingDetails.address.line1 || ''}
              onChange={handleOnChange}
              placeholder="Street" />
          </FormGroup>
          <FormGroup>
            <HalfSizeContainer>
              <LabelForInput htmlFor="customerCity">Apt, Unit</LabelForInput>
              <InputComponent
                type="text"
                id="address_line2"
                value={billingDetails.address.line2 || ''}
                onChange={handleOnChange}
                placeholder="City" />
            </HalfSizeContainer>
            <HalfSizeContainer>
              <LabelForInput htmlFor="customerCity">City</LabelForInput>
              <InputComponent
                type="text"
                id="address_city"
                value={billingDetails.address.city || ''}
                onChange={handleOnChange}
                placeholder="City" />
            </HalfSizeContainer>
          </FormGroup>

          <FormGroup>
            <HalfSizeContainer>
              <LabelForInput htmlFor="customerState">State</LabelForInput>
              <InputComponent
                type="text"
                id="address_state"
                value={billingDetails.address.state || ''}
                onChange={handleOnChange}
                placeholder="State" />
            </HalfSizeContainer>
            <HalfSizeContainer>
              <LabelForInput htmlFor="customerZipcode">Zipcode</LabelForInput>
              <InputComponent
                type="text"
                id="address_postal_code"
                value={billingDetails.address.postal_code || ''}
                onChange={handleOnChange}
                placeholder="Zipcode" />
            </HalfSizeContainer>

          </FormGroup></div>) : (null)}

        <Message>
          GABA filters out the noise and gives our members<br />
          back the most precious resource in a medical<br />
          student's arsenal: Time
          </Message>

        <FormGroup>
          <HalfSizeContainer>
            <BackButton
              onClick={cancelClick}>Back</BackButton>
          </HalfSizeContainer>
          <HalfSizeContainer>
            {showCheckout ? (
              <SelectButton
                type="Submit"
                disabled={isProcessing || (!stripe && !cardComplete)}
                className="btn btn-success btn-block">
                {isProcessing ? 'Processing...' : 'Complete Payment'}
              </SelectButton>
            ) : (<br />)}
          </HalfSizeContainer>
        </FormGroup>
      </form>
    </ContentContainer >
  )
};

const cardNumberElementOptions = {
  //a way to inject styles into that iframe
  style: {
    base: {
      color: 'rgb(0, 101, 242)',
      fontSmoothing: 'antialiased',
      fontSize: '14px',
      '::placeholder': {
        color: '#ccc',
      },
      border: '1px solid rgb(201, 205, 209)',
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true,
  placeholder: '4444 4444 4444 5555'
};

const expiryElementOptions = {
  //a way to inject styles into that iframe
  style: {
    base: {
      color: 'rgb(0, 101, 242)',
      fontSmoothing: 'antialiased',
      fontSize: '14px',
      '::placeholder': {
        color: '#ccc',
      },
      border: '1px solid rgb(201, 205, 209)',
      borderRadius: '6px',
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  }
};


const LabelForInput = styled.div`
  width: 100%;
  font-size:16px;
  padding:5px;
  font-family: Gotham-Medium;
  font-weight: 500;
  letter-spacing: 0px;
  line-height: 20px;  
`;

const FormGroup = styled.div`
  display:flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content:space-between;
  padding: 10px 15px; 

`;
const HalfSizeContainer = styled.div`
  width: 48%;
`;

const StripeInputContainer = styled.div`
  width: 100%;
  padding:5px;
  color: ${props => props.color || props.theme.color.darkBlue};
  background-color: ${props => props.backgroundColor || props.theme.color.softGray};  
  border: 1px solid rgb(201, 205, 209);
  border-radius: 6px;
  margin-top: 5px;
  outline: none;  
`;

const ContentContainer = styled.div`
  width: 100%;
  padding: 10px 10px;  
  color: ${props => props.theme.color.darkBlue};
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 150%;  
`;

const InputComponent = styled(Input)`
  width: 100%;
  padding: 15px 10px;
  font-size:16px;
  height: 24px;
  font-family: HelveticaNeue-Medium;
  font-weight: 500;
  letter-spacing: 0.1px;
  line-height: 24px;
  color: ${props => props.theme.color.darkBlue};
`

const SelectButton = styled(Button)`
  margin:0 auto;
  display:block;
  min-width: 100px;
  border-radius: 5px;
  font-size:16px;
  background: ${props => props.theme.color.gabaYellow};
  color: ${props => props.theme.color.darkBlue};
`
const BackButton = styled(SelectButton)`
  color: ${props => props.theme.color.darkBlue};
  background: ${props => props.theme.color.softPurple};
  border: 1px solid ${props => props.theme.color.softPurple};

`
const Message = styled.div`
  margin:0 auto;
  font-size:150%;
  padding: 10px 15px;
`;